import * as React from 'react'
import View from '@packages/common/src/components/View'
import { FormikBag, withFormik } from 'formik'
import * as yup from 'yup'
import { submissionErrorsHandler } from '@apps/accounts/src/utils/forms'
import errorToAlert from '@apps/accounts/src/utils/error-to-alert'
import FormEnhanced from '@packages/common/src/components/FormEnhanced'
import FormTextInput from '@packages/common/src/components/TextInput/FormTextInput'
import { SubmitButton } from '@packages/common/src/components/Button'
import { CmpStyles } from '@packages/common/src/providers/style/types'
import { compose } from 'recompose'
import passwordRecoveryQuery from '@packages/common/src/providers/network/queries/password-recovery/password-recovery'
import { useTranslation, withTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import withAbortSignalOnUnmount from '@packages/common/src/utils/hoc/with-abort-signal-on-unmount'
import { requestOpenApi } from '@packages/common/src/providers/network/client'
import {
  WithAlertActions,
  withAlertActions,
} from '@apps/accounts/src/components/AlertProvider'
import Link from '@apps/accounts/src/components/Link'
import Heading from '@packages/common/src/components/Heading'
import Text from '@packages/common/src/components/Text'
import { withRouter } from 'next/router'
import { WithRouterProps } from 'next/dist/client/with-router'
import { pathConstructor as signInPathConstructor } from '@apps/accounts/src/pages/index.helpers'
import { pathConstructor } from '@apps/accounts/src/pages/reset-password/sent.helpers'

type FormValues = {
  email: string
}

type Props = InnerProps & OuterProps

type InnerProps = WithAlertActions &
  WithRouterProps & {
    t: TFunction
    signal: AbortSignal
  }

type OuterProps = Record<string, never>

const handleSubmit = async (
  { email }: FormValues,
  { props: { signal, router } }: FormikBag<Props, FormValues>
) => {
  await requestOpenApi(passwordRecoveryQuery(email), { signal })
  router.push(pathConstructor.index(email))
}

const handleSubmissionErrors = submissionErrorsHandler<Props, FormValues>(
  {},
  (error, { props: { setAlert } }) => {
    setAlert(errorToAlert(error))
  }
)

const validationSchema = ({ t }: Props) =>
  yup.object().shape<Partial<FormValues>>({
    email: yup
      .string()
      .email(t('request-password-reset.form.validations.email.email'))
      .required(t('request-password-reset.form.validations.email.required')),
  })

const initialValues: FormValues = {
  email: '',
}

const styles: CmpStyles = {
  spaces: {
    flexDirection: 'column',

    nested: {
      '&>:nth-child(1)': {
        marginBottom: 20,
      },
      '&>:nth-child(2)': {
        marginBottom: 30,
      },
      '&>:last-child': {
        marginBottom: 20,
      },
    },
  },
  inputs: {
    display: 'grid',
    gridTemplateColumns: '75% 25%',
    columnGap: 10,
    alignItems: 'start',
    marginBottom: 30,
  },
}

const Form = () => {
  const { t } = useTranslation()
  const backPath = signInPathConstructor.forceSignIn()

  return (
    <View as={FormEnhanced} style={styles.spaces} testID="reset-password-form">
      <Heading>{t('request-password-reset.form.title')}</Heading>

      <Text>{t('request-password-reset.form.description')}</Text>

      <View style={styles.inputs}>
        <FormTextInput
          name="email"
          placeholder={t('request-password-reset.form.email-placeholder')}
          maxLength={255}
        />

        <SubmitButton accent="secondary">
          {t('request-password-reset.form.submit')}
        </SubmitButton>
      </View>

      <View>
        <Link to={backPath} scale={-1} weight="bold" accent="default">
          {t('request-password-reset.to-sign-in')}
        </Link>
      </View>
    </View>
  )
}

export default compose<Props, OuterProps>(
  withAbortSignalOnUnmount,
  withAlertActions,
  withRouter,
  withTranslation(),
  withFormik<Props, FormValues>({
    validationSchema,
    handleSubmit: handleSubmissionErrors(handleSubmit),
    validateOnBlur: false,
    validateOnChange: false,
    mapPropsToValues: () => initialValues,
  })
)(Form)
